import { forwardRef, React, useEffect, useImperativeHandle, useRef, useState } from 'react';
import styled, { keyframes, css }from 'styled-components';

import AccountInput from '../../../Component/AccountComponent/AccountInput.js';
import SignUpSelectBox from '../../../Component/AccountComponent/SignUpSelectBox.js';
import { useLanguage } from '../../../Context/LanguageContext';

const SignUpInfo3 = forwardRef((props, ref) => {
	const { t } = useLanguage();
	const [storeClickCount, setStoreClickCount] = useState(0);
	const [typeClickCount, setTypeClickCount] = useState(0);
	const [telClickCount, setTelClickCount] = useState(0);
	const [isAnimate, setIsAnimate] = useState(false);

	const TopLevelRef = useRef();
	const StoreRef = useRef();
	const TypeRef = useRef();
	const TelRef = useRef();
	const StorePlanationRef = useRef();
	const TelPlanationRef = useRef();

	useImperativeHandle(ref, () => ({
		enableEnterAnimation: () => {
			setIsAnimate(true);
		},
		disableEnterAnimation: () => {
			setIsAnimate(false);
		},
		getStoreName: () => {
			return StoreRef.current.getValue();
		},
		getStoreType: () => {
			return TypeRef.current.getValue();
		},
		getStoreTel: () => {
			return TelRef.current.getValue();
		},
		isBlankStoreName: () => {
			StoreRef.current.setDefaultPlaceholder(t('signUp.storeNamePlaceholder'));
			StoreRef.current.plusClickCount();
			StorePlanationRef.current.style.opacity = 1;
		},
		isBlankStoreTel: () => {
			const tel = (TelRef.current.getValue() || '').trim();

			TelRef.current.setDefaultPlaceholder(t('signUp.storeTelPlaceholder'));
			TelRef.current.plusClickCount();

			if (tel.length === 0) {
				TelPlanationRef.current.innerText = t('signUp.requiredField');
				TelPlanationRef.current.style.opacity = 1;
				return;
			}

			TelPlanationRef.current.style.opacity = 0;
		},
		// 상호명 유효성 검증 (에러 표시하고 boolean 반환)
		isValidStoreName: () => {
			const store = (StoreRef.current.getValue() || '').trim();

			if (store === '') {
				StoreRef.current.setDefaultPlaceholder(t('signUp.storeNamePlaceholder'));
				StoreRef.current.plusClickCount();
				StorePlanationRef.current.innerText = t('signUp.requiredField');
				StorePlanationRef.current.style.opacity = 1;

				return false;
			}

			return true;
		},
		// 전화번호 유효성 검증 (에러 표시하고 boolean 반환)
		isValidStoreTel: () => {
			const tel = (TelRef.current.getValue() || '').trim();

			TelRef.current.setDefaultPlaceholder(t('signUp.storeTelPlaceholder'));
			TelRef.current.plusClickCount();

			if (tel.length === 0) {
				TelPlanationRef.current.innerText = t('signUp.requiredField');
				TelPlanationRef.current.style.opacity = 1;

				return false;
			}

			return true;
		},
		setOpacity: (val) => {
			TopLevelRef.current.style.opacity = val;
			if (val === 0) TopLevelRef.current.setAttribute('inert', '');
			else TopLevelRef.current.removeAttribute('inert');
		},
		setLocation: (val) => {
			TopLevelRef.current.style.transform = `translateX(${val}%)`;
		}
	}));

	useEffect(() => {
		TopLevelRef.current.setAttribute('inert', '');
	}, []);

	useEffect(() => {
		if (storeClickCount === 0 && telClickCount === 0)
			return;

		// 상호명
		const store = (StoreRef.current.getValue() || '').trim();

		if (store === '' && storeClickCount > 0) {
			StorePlanationRef.current.innerText = t('signUp.requiredField');
			StorePlanationRef.current.style.opacity = 1;
		}
		else {
			StorePlanationRef.current.style.opacity = 0;
		}

		// 전화번호
		const tel = (TelRef.current.getValue() || '').trim();

		if (telClickCount > 0) {
			if (tel.length === 0) {
				TelPlanationRef.current.innerText = t('signUp.requiredField');
				TelPlanationRef.current.style.opacity = 1;
			}
			else {
				// 유효한 전화번호: 에러 메시지 숨기고 border를 회색으로 설정
				TelPlanationRef.current.style.opacity = 0;
				TelRef.current.reset(t('signUp.storeTelPlaceholder'));
			}
		}
	}, [storeClickCount, telClickCount]);

	function clickCountFn(className, clickCount) {
		if (className === 'StoreInput')
			setStoreClickCount(clickCount);
		else if (className === 'TelInput')
			setTelClickCount(clickCount);
	}

	return (
		<TopLevelWrapper ref={TopLevelRef} $animate={isAnimate}>
			<InputInfo>{t('signUp.storeName')}</InputInfo>
			<AccountInput ref={StoreRef} className='StoreInput' placeholder={t('signUp.storeNamePlaceholder')} type='text' clickCountFn={clickCountFn}/>
			<AccountExplanation ref={StorePlanationRef}>{t('signUp.requiredField')}</AccountExplanation>
			<InputInfo isSelect={true}>{t('signUp.centerType')}</InputInfo>
			<SignUpSelectBox ref={TypeRef} className='TypeInput'/>
			<Padding />
			<InputInfo>{t('signUp.storeTel')}</InputInfo>
			<AccountInput ref={TelRef} className='TelInput' placeholder={t('signUp.storeTelPlaceholder')} type='text' clickCountFn={clickCountFn}/>
			<AccountExplanation ref={TelPlanationRef}>{t('signUp.requiredField')}</AccountExplanation>
		</TopLevelWrapper>
	)
})

export default SignUpInfo3;

const startAnimation = keyframes`
	0% { opacity: 0; transform: translateX(150%); }
	10% { opacity: 0.1; transform: translateX(130%); }
	20% { opacity: 0.2; transform: translateX(100%); }
	30% { opacity: 0.3; transform: translateX(70%); }
	40% { opacity: 0.4; transform: translateX(40%); }
	50% { opacity: 0.5; transform: translateX(20%); }
	60% { opacity: 0.6; transform: translateX(0%); }
	70% { opacity: 0.7;}
	80% { opacity: 0.8; }
	90% { opacity: 0.9;}
	100% { opacity: 1;}
`;


const TopLevelWrapper = styled.div`
	position: absolute;
	display: flex;

	flex-direction: column;
	justify-content: flex-start;

	width: 480px;
	// width: max(271px, 24.375vw);
	// height: 281px;

	margin-top: 146px;
	// margin-top: max(100px, 8.28125vw);

	background-color: ${props => props.theme.subBackground};

	opacity: 0;
	z-index: 2;
	transform: translateX(150%);

	animation: none;

	${(props) =>
			props.$animate &&
			css`
				animation: ${startAnimation} 0.5s ease-out;
	`}

	transition: all 0.3s ease-in-out;
`;

const InputInfo = styled.p`
	font-family: NotoSansKR-Regular;
	font-size: 16px;
	// font-size: max(12px, 0.7291666666666667vw);
	// line-height: 20px;
 
	// margin: 40px 0 0 0;
	margin: ${props => props.tel ? `80px 0 0 0` : `20px 0 0 0`};
	// margin-top: ${props => props.isSelect ? `max(31.73px, 2.083333333333333vw)` : `19px`};
	// margin-top: 16px;
	// margin-right: 0px;
	// margin-bottom: 0px;
	// margin-left: 0px;

	cursor: default;

	color: ${props => props.theme.text};
`;

const AccountExplanation = styled.p`
	font-family: NotoSansKR-Regular;
	font-size: 14px;
	// font-size: max(12px, 0.625vw);
	// line-height: 17.38px;

	color: ${props => props.theme.pointRed};

	margin: 4px 0 0 0;

	opacity: 0;
  
	transition: all 0.5s ease-in-out;
`;

const Padding = styled.div`
	height: 25px;
`;